import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { List, LayoutGrid, Search, ArrowLeft, Box, Wrench, ShoppingCart, Trash2, Pause, CreditCard, Loader2, Folder, X, ArrowRightLeft, Boxes, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../../Router/all_routes";
import { hiddenRoutes } from "../../../../config/profiles";
import CategoryCard from "./CategoryCard";
import ProductCard from "./ProductCard";
import LoadingWrapper from "../../../../core/components/LoadingWrapper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const ProductGridSkeleton = ({ count = 10 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-5 gap-3 sm:gap-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="flex flex-col space-y-3">
        <Skeleton className="h-[150px] w-full rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    ))}
  </div>
);

const ProductView = ({
  activeTab,
  handleTabChange,
  hasPermission,
  currentCat,
  currentCatName = "",
  categorySearch = "",
  onCategorySearchChange = () => { },
  categoriesLoading = false,
  categories,
  subcategories = [],
  subcategoriesLoading = false,
  handleCategorySelect,
  selectedBrand,
  brands,
  allProductsSearch = "",
  onAllProductsSearchChange = () => { },
  products,
  productsLoading,
  isFetchingMore = false,
  filterProductsByPrice,
  addCartItem,
  currencySymbol,
  hasMoreProducts = false,
  onLoadMore = () => { },
  onCancelOrder = () => { },
  onRecentOrders = () => { },
  onHoldOrder = () => { },
  onCompleteOrder = () => { },
  completing = false,
  currentSaleData = null,
  balanceDue = 0,
  onExitSale = () => { },
}) => {
  const navigate = useNavigate();

  const displayedCategories = useMemo(() => {
    if (categorySearch && categorySearch.trim()) {
      const term = categorySearch.toLowerCase().trim();
      return categories.filter((c) => c.name?.toLowerCase().includes(term));
    }
    return categories.filter(
      (c) => !c.parent_id || c.parent_id === "0" || c.parent_id === 0,
    );
  }, [categories, categorySearch]);

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] bg-muted/20 p-2 xl:p-4 overflow-hidden">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex flex-col flex-1 min-h-0 space-y-2 xl:space-y-3"
      >
        <div className="flex flex-col space-y-2 xl:space-y-3 flex-shrink-0">
          {/* Top Bar: 1. Sales  2. Repairs  3. Trade In  4. Miscellaneous */}
          <Tabs value="sales" className="w-full">
            <TabsList
              className={`grid w-full h-10 p-1 bg-muted rounded-lg border border-border/50 shadow-xs ${hiddenRoutes.has("repair-pos") ? "grid-cols-3" : "grid-cols-4"
                }`}
            >
              <TabsTrigger
                value="sales"
                className="flex items-center justify-center gap-2 h-8 text-sm font-semibold rounded-md cursor-pointer transition-all"
              >
                <ShoppingCart size={15} />
                <span>Sales</span>
              </TabsTrigger>
              {!hiddenRoutes.has("repair-pos") && (
                <TabsTrigger
                  value="repairs"
                  onClick={() => navigate(all_routes.repairPos)}
                  className="flex items-center justify-center gap-2 h-8 text-sm font-medium rounded-md text-muted-foreground hover:text-foreground hover:bg-background/40 transition-all cursor-pointer"
                >
                  <Wrench size={15} />
                  <span>Repairs</span>
                </TabsTrigger>
              )}
              <div
                title="Feature coming soon"
                className="flex items-center justify-center cursor-not-allowed"
              >
                <TabsTrigger
                  disabled
                  value="trade-in"
                  className="w-full flex items-center justify-center gap-2 h-8 text-sm font-medium rounded-md opacity-50 cursor-not-allowed pointer-events-none"
                >
                  <ArrowRightLeft size={15} />
                  <span>Trade In</span>
                </TabsTrigger>
              </div>
              <div
                title="Feature coming soon"
                className="flex items-center justify-center cursor-not-allowed"
              >
                <TabsTrigger
                  disabled
                  value="miscellaneous"
                  className="w-full flex items-center justify-center gap-2 h-8 text-sm font-medium rounded-md opacity-50 cursor-not-allowed pointer-events-none"
                >
                  <Boxes size={15} />
                  <span>Miscellaneous</span>
                </TabsTrigger>
              </div>
            </TabsList>
          </Tabs>

          {/* Second Bar: Filters (By Category, All Products) */}
          <div className="flex items-center gap-3 p-1.5 px-2.5 bg-card border border-border rounded-lg shadow-2xs">
            <TabsList
              className="grid max-w-[320px] h-8 p-0.5 bg-muted/80 rounded-md border border-border/40"
              style={{
                gridTemplateColumns: hasPermission("category product read")
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(1, minmax(0, 1fr))",
              }}
            >
              {hasPermission("category product read") && (
                <TabsTrigger
                  value="by-category"
                  className="flex items-center gap-1.5 text-xs font-medium h-7 rounded-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs text-muted-foreground hover:text-foreground transition-all cursor-pointer"
                >
                  <LayoutGrid size={14} />
                  <span>By Category</span>
                </TabsTrigger>
              )}
              <TabsTrigger
                value="all-products"
                className="flex items-center gap-1.5 text-xs font-medium h-7 rounded-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs text-muted-foreground hover:text-foreground transition-all cursor-pointer"
              >
                <List size={14} />
                <span>All Products</span>
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-h-0 bg-transparent">
          <TabsContent
            value="by-category"
            className="h-full m-0 data-[state=active]:flex flex-col"
          >
            {hasPermission("category product read") ? (
              <div className="flex flex-col h-full overflow-hidden">
                {/* Category Search Bar - visible both at root and inside category */}
                <div className="relative mb-3 flex-shrink-0">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={
                      currentCat ? "Search subcategories..." : "Search categories..."
                    }
                    className="h-9 pl-8 pr-8 text-sm bg-card border-border"
                    value={categorySearch}
                    onChange={(e) => onCategorySearchChange(e.target.value)}
                  />
                  {categorySearch && (
                    <button
                      type="button"
                      className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                      onClick={() => onCategorySearchChange("")}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>

                {!currentCat ? (
                  // Category Listing
                  <ScrollArea className="flex-1 pr-2 xl:pr-4">
                    {categoriesLoading ? (
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4 pb-4">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <div
                            key={i}
                            className="aspect-square w-full rounded-xl bg-muted/40 animate-pulse border border-border/50"
                          />
                        ))}
                      </div>
                    ) : displayedCategories.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                        <Folder size={48} className="mb-3 opacity-20" />
                        <p className="text-sm">
                          {categorySearch
                            ? `No categories found matching "${categorySearch}"`
                            : "No categories found."}
                        </p>
                        {categorySearch && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3 text-xs"
                            onClick={() => onCategorySearchChange("")}
                          >
                            Clear Search
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4 pb-4">
                        {displayedCategories.map((category) => (
                          <CategoryCard
                            key={category.id}
                            category={category}
                            handleCategorySelect={(id) =>
                              handleCategorySelect(id, category.name)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                ) : (
                  // Products Section for Selected Category
                  <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    <div className="flex items-center justify-between pb-3 flex-shrink-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const current = categories.find(
                              (c) => String(c.id) === String(currentCat),
                            );
                            const parentId =
                              current?.parent_id &&
                                current.parent_id !== "0" &&
                                current.parent_id !== 0
                                ? current.parent_id
                                : null;
                            handleCategorySelect(parentId);
                          }}
                          className="h-8 gap-1.5"
                        >
                          <ArrowLeft size={14} />
                          Back
                        </Button>
                        <Badge variant="secondary" className="font-normal">
                          Category:{" "}
                          {currentCat === "all"
                            ? "All Categories"
                            : currentCatName ||
                            categories.find(
                              (c) => String(c.id) === String(currentCat),
                            )?.name ||
                            "All"}
                        </Badge>
                        {selectedBrand && (
                          <Badge variant="secondary" className="font-normal">
                            Brand:{" "}
                            {brands.find((b) => b.id === selectedBrand)
                              ?.name || ""}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {subcategoriesLoading ? (
                      <div className="p-2.5 mb-3 rounded-lg bg-muted/30 border border-border/50 shrink-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h6 className="text-xs font-semibold text-muted-foreground">
                            Subcategories
                          </h6>
                          <Loader2 size={12} className="animate-spin text-muted-foreground" />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4">
                          {Array.from({ length: 4 }).map((_, i) => (
                            <div
                              key={i}
                              className="aspect-square w-full rounded-xl bg-muted/40 animate-pulse border border-border/50"
                            />
                          ))}
                        </div>
                      </div>
                    ) : subcategories.length > 0 ? (
                      <div className="p-2.5 mb-3 rounded-lg bg-muted/30 border border-border/50 shrink-0">
                        <h6 className="text-xs font-semibold mb-2 text-muted-foreground">
                          Subcategories ({subcategories.length})
                        </h6>
                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4">
                          {subcategories.map((category) => (
                            <CategoryCard
                              key={category.id}
                              category={category}
                              handleCategorySelect={(id) =>
                                handleCategorySelect(id, category.name)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ) : subcategories.length === 0 && categorySearch ? (
                      <div className="p-3 mb-3 rounded-lg bg-muted/20 border border-border/40 shrink-0 text-center">
                        <p className="text-xs text-muted-foreground">
                          No subcategories found matching "{categorySearch}".
                        </p>
                      </div>
                    ) : null}

                    <ScrollArea className="flex-1 pr-2 xl:pr-4">
                      <LoadingWrapper
                        isLoading={productsLoading}
                        skeleton={<ProductGridSkeleton count={10} />}
                      >
                        {products.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                            <Box size={48} className="mb-3 opacity-20" />
                            <p>No products found in this category.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4 pb-4">
                            {filterProductsByPrice(products)
                              .filter(
                                (product) => product.display_on_pos === true,
                              )
                              .map((product) => (
                                <ProductCard
                                  key={product.id}
                                  product={product}
                                  addCartItem={addCartItem}
                                  currencySymbol={currencySymbol}
                                />
                              ))}
                          </div>
                        )}
                        {hasMoreProducts && products.length > 0 && (
                          <div className="flex justify-center py-4">
                            <Button
                              variant="outline"
                              onClick={onLoadMore}
                              disabled={productsLoading || isFetchingMore}
                            >
                              {isFetchingMore ? "Loading..." : "Load More"}
                            </Button>
                          </div>
                        )}
                      </LoadingWrapper>
                    </ScrollArea>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <h5>You do not have permission to view categories.</h5>
              </div>
            )}
          </TabsContent>

          <TabsContent
            value="all-products"
            className="h-full m-0 data-[state=active]:flex flex-col"
          >
            <div className="flex flex-col h-full overflow-hidden">
              {/* Dedicated All Products Search Bar */}
              <div className="relative mb-3 flex-shrink-0">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search all products by name, SKU, or barcode..."
                  className="h-9 pl-8 pr-8 text-sm bg-card border-border"
                  value={allProductsSearch}
                  onChange={(e) => onAllProductsSearchChange(e.target.value)}
                />
                {allProductsSearch && (
                  <button
                    type="button"
                    className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={() => onAllProductsSearchChange("")}
                    title="Clear search"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              {(selectedBrand || allProductsSearch) && (
                <div className="flex items-center gap-2 pb-3 flex-shrink-0 flex-wrap">
                  {selectedBrand && (
                    <Badge variant="secondary" className="font-normal">
                      Brand:{" "}
                      {brands.find((b) => b.id === selectedBrand)?.name || ""}
                    </Badge>
                  )}
                  {allProductsSearch && (
                    <Badge variant="secondary" className="font-normal">
                      Search: {allProductsSearch}
                    </Badge>
                  )}
                </div>
              )}

              <ScrollArea className="flex-1 pr-2 xl:pr-4">
                <LoadingWrapper
                  isLoading={productsLoading}
                  skeleton={<ProductGridSkeleton count={10} />}
                >
                  {products.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Search size={48} className="mb-3 opacity-20" />
                      <p>
                        {allProductsSearch
                          ? `No products found matching "${allProductsSearch}"`
                          : "No products found."}
                      </p>
                      {allProductsSearch && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 text-xs"
                          onClick={() => onAllProductsSearchChange("")}
                        >
                          Clear Search
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4 pb-4">
                      {filterProductsByPrice(products)
                        .filter((product) => product.display_on_pos === true)
                        .map((product) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            addCartItem={addCartItem}
                            currencySymbol={currencySymbol}
                          />
                        ))}
                    </div>
                  )}
                  {hasMoreProducts && products.length > 0 && (
                    <div className="flex justify-center py-4">
                      <Button
                        variant="outline"
                        onClick={onLoadMore}
                        disabled={productsLoading || isFetchingMore}
                      >
                        {isFetchingMore ? "Loading..." : "Load More"}
                      </Button>
                    </div>
                  )}
                </LoadingWrapper>
              </ScrollArea>
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Action Buttons: Right Side Bottom with 1 card height gap */}
      <div className="flex-shrink-0 mt-20">
        <div className="p-2 sm:p-2.5 bg-card border border-border rounded-xl shadow-sm">
          <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
            <Button
              variant="outline"
              className="h-12 border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive hover:text-white rounded-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={currentSaleData ? onExitSale : onCancelOrder}
              title={currentSaleData ? "Exit Sale View and start a new cart" : "Cancel Order"}
            >
              {currentSaleData ? <X size={16} /> : <Trash2 size={16} />}
              <span>{currentSaleData ? "Exit Sale" : "Cancel"}</span>
            </Button>

            <Button
              variant="outline"
              className="h-12 border border-border bg-muted/80 text-foreground hover:bg-muted rounded-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={onRecentOrders}
              title="Recent Orders"
            >
              <List size={16} />
              <span>Recent</span>
            </Button>

            <Button
              variant="outline"
              disabled={Boolean(currentSaleData)}
              className="h-12 border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500 hover:text-white rounded-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold cursor-pointer shadow-xs active:scale-[0.98] disabled:opacity-40"
              onClick={onHoldOrder}
              title={currentSaleData ? "Cannot hold a finalized sale" : "Hold Order"}
            >
              <Pause size={16} />
              <span>Hold</span>
            </Button>

            {hasPermission("pos payment create") && (
              <Button
                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold cursor-pointer shadow-md active:scale-[0.98] border-none"
                onClick={onCompleteOrder}
                disabled={completing}
                title={
                  currentSaleData
                    ? balanceDue > 0.001
                      ? "Pay Remaining Balance"
                      : "View / Print Invoice"
                    : "Checkout"
                }
              >
                {completing ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : currentSaleData ? (
                  balanceDue > 0.001 ? (
                    <CreditCard size={18} />
                  ) : (
                    <FileText size={18} />
                  )
                ) : (
                  <CreditCard size={18} />
                )}
                <span>
                  {completing
                    ? "Opening..."
                    : currentSaleData
                      ? balanceDue > 0.001
                        ? `Pay Balance`
                        : "Invoice"
                      : "Checkout"}
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ProductView.propTypes = {
  activeTab: PropTypes.string.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
  currentCat: PropTypes.any,
  currentCatName: PropTypes.string,
  categorySearch: PropTypes.string,
  onCategorySearchChange: PropTypes.func,
  categoriesLoading: PropTypes.bool,
  categories: PropTypes.array,
  subcategories: PropTypes.array,
  subcategoriesLoading: PropTypes.bool,
  handleCategorySelect: PropTypes.func.isRequired,
  selectedBrand: PropTypes.any,
  brands: PropTypes.array,
  allProductsSearch: PropTypes.string,
  onAllProductsSearchChange: PropTypes.func,
  products: PropTypes.array,
  productsLoading: PropTypes.bool,
  isFetchingMore: PropTypes.bool,
  filterProductsByPrice: PropTypes.func,
  addCartItem: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  recentProducts: PropTypes.array,
  hasMoreProducts: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onCancelOrder: PropTypes.func,
  onRecentOrders: PropTypes.func,
  onHoldOrder: PropTypes.func,
  onCompleteOrder: PropTypes.func,
  completing: PropTypes.bool,
  currentSaleData: PropTypes.object,
  balanceDue: PropTypes.number,
  onExitSale: PropTypes.func,
};

export default ProductView;
